import {
    MenuItem,
    Select,
    OutlinedInput,
    InputAdornment,
    Chip,
    Link,
    SelectChangeEvent,
    IconButton,
    Button,
    Autocomplete,
    TextField,
    Switch,
    Stack,
    Box,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import { cacheService, orderService, rightService } from "../../../Core/Api/services";
import TableCellMovie from "../../../Components/Table/TableCellMovie/table-cell-movie";
import { routePaths } from "../../../Router/router";
import TableCellDateTime from "../../../Components/Table/TableCellDateTime/table-cell-date-time";
import ListPageHeader, {
    ListPageHeaderGroupActions,
    ListPageHeaderLeft,
    ListPageHeaderRight,
} from "../../../Components/Misc/ListPageHeader";
import AutocompleteSearchCatalog from "../../../Components/Search/AutocompleteSearchCatalog/autocomplete-search-catalog";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import DataTable, { CellSize } from "../../../Components/Table/table";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import { Link as RouterLink, useOutletContext } from "react-router";
import TableCellEnabled from "../../../Components/Table/TableCellEnabled/table-cell-enabled";
import ContractDetailsPanel from "../../../Components/Panels/ContractDetailsPanel/contract-panel";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import moment from "moment";
import { DbCustomerInfos } from "../../../Core/Api/Types/db-customer-infos";
import { ContractAndRightType } from "../../../Core/Api/Right/right-api-service";
import { MakeUnique } from "../../../Utils/helpers";
import { RightType } from "../../../Core/Api/Types/right-type";
import { TitleType } from "../../../Core/Api/Types/title-type";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import { CopyrightOutlined, Inventory2Outlined } from "@mui/icons-material";
import TableUuids from "../../../Components/Table/table-uuids";

function ContractsAndRights() {
    const { t } = useTranslation();
    //   const { active: contractDetailsPanelOpen, toggle: toggleContractDetailsPanelOpen } = useToggle(false);
    const [selectedContract, setSelectedContract] = React.useState<ContractAndRightType>();
    const [contractDetailsPanelOpen, setContractDetailsPanelOpen] = React.useState(false);

    const { addContractsToBasket } = useBasketActions();
    const maxItems = 25;
    const [selection, setSelection] = React.useState<number[]>([]);

    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [rights, setRights] = React.useState<ContractAndRightType[]>([]);
    const [initialRights, setInitialRights] = React.useState<ContractAndRightType[]>([]);

    const [titleTypes, setTitleTypes] = React.useState<string[]>([]);
    const [titleTypeFilter, setTitleTypeFilter] = React.useState<string>("Default");
    const [types, setTypes] = React.useState<string[]>([]);
    const [typeFilter, setTypeFilter] = React.useState<string>("Default");
    const [delivered, setDelivered] = React.useState<string[]>([]);
    const [deliveredFilter, setDeliveredFilter] = React.useState<string>("Default");
    const [selectedContractTds, setSelectedContractIds] = React.useState<number[]>([]);
    const [externalIdFilter, setExternalIdFilter] = React.useState<string>("");
    const [customers, setCustomers] = React.useState<DbCustomerInfos[]>([]);
    const [selectedCustomer, setSelectedCustomer] = React.useState<DbCustomerInfos>();
    const [filterMediaNames, setFilterMediaNames] = React.useState<string[]>([]);
    const [groupByTitle, setGroupByTitle] = React.useState(false);

    const metadataFields = React.useMemo(() => cacheService.GetMetadata(), []);

    const apiGrid = useGridApiRef();
    const enqeueCustomSnack = useCustomSnackbar();

    // Outlet context used to pass props to parent DashboardLayout through react router dom
    const { setSelectedContracts, selectedContracts } = useOutletContext<{
        setSelectedContracts: React.Dispatch<React.SetStateAction<ContractAndRightType[]>>;
        selectedContracts: ContractAndRightType[];
    }>();

    // Filters
    React.useEffect(() => {
        setTitleTypes(Object.values(["Movie", "Season"]));
        setTypes(Object.values(["Disposal", "Acquisition"]));
        setDelivered(Object.values(["True", "False"]));
    }, []);

    // Fetching data
    React.useEffect(() => {
        orderService.GetCustomersList().then((res) => {
            if (res && res.customers) {
                setCustomers(res.customers);
                rightService.GetRightContracts(maxItems, metadataFields, false).then((response) => {
                    setRights(
                        response.map((r, i) => ({
                            ...r,
                            id: i,
                            customerName: res.customers?.find((c) => c.id === r.customerId)?.name || "Unknown",
                        }))
                    );
                    setInitialRights(
                        response.map((r, i) => ({
                            ...r,
                            id: i,
                            customerName: res.customers?.find((c) => c.id === r.customerId)?.name || "Unknown",
                        }))
                    );
                    setIsLoading(false);
                });
            } else {
                setIsLoading(false);
                enqeueCustomSnack(t("alert:error.fetchingCustomersData"), "error");
            }
        });
    }, []);

    const getColumns = React.useMemo(() => {
        const cols: GridColDef[] = [
            {
                field: "contractId",
                headerName: "Id",
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                renderCell: (data: any) => (
                    <Link
                        color="primary.text"
                        underline="hover"
                        component={RouterLink}
                        to={`${routePaths.contractsAndRights.path}/${data.value}`}
                        target="_blank"
                    >
                        {data.value}
                    </Link>
                ),
            },
            {
                field: "titleInfos",
                headerName: t("common:title"),
                flex: CellSize.xlarge,
                minWidth: CellSize.xlarge,
                valueGetter: (data: any, row: any) => row?.titleInfos?.name + " " + data?.row?.titleInfos?.id,
                renderCell: (data: any) => <TableCellMovie id={data?.row?.titleInfos?.id} name={data?.row?.titleInfos?.name} />,
            },
            {
                field: "titleType",
                headerName: t("field:label.titleTypeLowerCase"),
                type: "singleSelect",
                valueOptions: Object.keys(TitleType),
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data: any) => (
                    <Chip
                        size="small"
                        icon={data.value === "Movie" ? <MovieOutlinedIcon /> : <LiveTvIcon />}
                        color={data.value === "Movie" ? "info" : "warning"}
                        label={data.value}
                    />
                ),
            },
        ];

        if (!groupByTitle) {
            cols.push(
                {
                    field: "customerName",
                    headerName: t("common:customer"),
                    flex: CellSize.large,
                    minWidth: CellSize.large,
                    type: "singleSelect",
                    valueOptions:
                        rights
                            ?.map((x) => x.customerName || "")
                            .filter((x, i) => rights?.map((a) => a.customerName || "").indexOf(x) === i && x !== "") || [],
                    renderCell: (data: any) => (
                        <Link
                            href={`${routePaths.customers.path}/${data?.row?.customerId}`}
                            color="primary.text"
                            underline="hover"
                            sx={{ fontSize: 12 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {data.value}
                        </Link>
                    ),
                },
                {
                    field: "type",
                    headerName: t("common:type"),
                    type: "singleSelect",
                    valueOptions: Object.keys(RightType),
                    flex: CellSize.small,
                    minWidth: CellSize.small,
                    renderCell: (data: any) => <Chip size="small" label={data.value} />,
                },
                {
                    field: "startDate",
                    headerName: t("common:startDateLowerCase"),
                    type: "date",
                    valueGetter: (data: any, row: any) => {
                        if (!row) return;
                        return new Date(row?.startDate);
                    },

                    flex: CellSize.medium,
                    minWidth: CellSize.medium,
                    renderCell: (data: any) => (
                        <TableCellDateTime
                            data={{ ...data, value: moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS) }}
                        />
                    ),
                },
                {
                    field: "endDate",
                    headerName: t("common:endDateLowerCase"),
                    flex: CellSize.medium,
                    minWidth: CellSize.medium,
                    type: "date",
                    valueGetter: (data: any, row: any) => {
                        if (!row) return;
                        return new Date(row?.endDate);
                    },
                    renderCell: (data: any) => (
                        <TableCellDateTime
                            data={{ ...data, value: moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS) }}
                        />
                    ),
                },
                {
                    field: "okDelivery",
                    headerName: t("common:delivered"),
                    type: "boolean",
                    flex: CellSize.small,
                    minWidth: CellSize.small,
                    renderCell: (data: any) => <TableCellEnabled value={data.value} />,
                },
                {
                    field: "languages",
                    headerName: t("common:languages"),
                    flex: CellSize.small,
                    minWidth: CellSize.small,
                    type: "number",
                    align: "center",
                    headerAlign: "center",
                    valueGetter: (data: any, row: any) => row?.languages?.length || 0,
                    renderCell: (data: any) => (
                        <Chip color="primary" variant="outlined" size="small" label={data.value} sx={{ ml: 1 }} />
                    ),
                },
                {
                    field: "territories",
                    headerName: t("common:territories"),
                    type: "number",
                    align: "center",
                    headerAlign: "center",
                    valueGetter: (data: any, row: any) => row?.territories?.length || 0,
                    flex: CellSize.small,
                    minWidth: CellSize.small,
                    renderCell: (data: any) => (
                        <Chip color="primary" variant="outlined" size="small" label={data.value} sx={{ ml: 1 }} />
                    ),
                }
            );
        } else {
            cols.push({
                field: "dealsNumber",
                headerName: t("table:headerName.dealsCount"),
                type: "number",
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                valueGetter: (data: any, row: any) => rights.filter((x) => x.titleId === row?.titleInfos?.id).length,
                renderCell: (data: any) => <Chip color="primary" size="small" label={data.value} sx={{ ml: 1 }} />,
            });
        }

        cols.push({
            field: "actions",
            headerName: "",
            type: "actions",
            align: "right",
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<ShoppingBagOutlinedIcon sx={{ width: 20, height: 20 }} />}
                    label={t("common:addToBasket")}
                    onClick={() => handleAddToBasket(params.row)}
                />,
            ],
        });

        return cols;
    }, [groupByTitle, rights, isLoading]);

    const mediaNamesOptions = useMemo(() => {
        let mediaNames = rights.map((x) => (x.mediaNamesString ? x.mediaNamesString : "")).join(",");

        return [
            ...new Set(
                mediaNames
                    .split(",")
                    .map((str) => str.trim())
                    .filter((x) => x !== "")
            ),
        ];
    }, [rights]);

    const handleAddToBasket = (row: ContractAndRightType) => {
        let basketElements: ContractAndRightType[] = [];

        if (selection === undefined) {
            basketElements.push(row);
        } else {
            basketElements = rights.filter((x) => selection.includes(x.id));
        }
        // add item not selected but concerned
        if (selection && !selection.includes(row.id)) {
            basketElements.push(row);
        }
        addContractsToBasket(basketElements);
    };

    const handleTitleTypeFilter = (event: SelectChangeEvent<string>) => {
        setTitleTypeFilter(event.target.value as string);
    };

    const handleTypeFilter = (event: SelectChangeEvent<string>) => {
        setTypeFilter(event.target.value as string);
    };

    const handleDeliveredFilter = (event: SelectChangeEvent<string>) => {
        setDeliveredFilter(event.target.value as string);
    };

    function setOnSearchEnter() {
        setIsLoading(true);
    }

    function setDataFromSearchCallback(data: ContractAndRightType[]) {
        setExternalIdFilter("");
        setRights(
            data.map((r, i) => ({
                ...r,
                id: i,
            }))
        );
        setIsLoading(false);
    }

    const handleSearchByContractId = () => {
        setIsLoading(true);
        rightService.GetRightContractsByDealExternalId(externalIdFilter, metadataFields).then((response) => {
            setRights(
                response.map((r, i) => ({
                    ...r,
                    id: i,
                }))
            );
            setIsLoading(false);
        });
    };

    const handleSearchByCustomer = (id: number) => {
        setIsLoading(true);

        rightService.GetRightContractsByCustomerId(id, metadataFields, false).then((response) => {
            setRights(
                response.map((r, i) => ({
                    ...r,
                    id: i,
                }))
            );
            setIsLoading(false);
        });
    };

    const handleOnKeyDown = (event: { key: string }) => {
        if (event.key === "Enter") {
            handleSearchByContractId();
        }
    };

    const handleSetSelectedContracts = useCallback(
        (ids: number[]) => {
            setSelectedContractIds(ids);
            let contracts = rights.filter((x) => ids.includes(x.id));
            setSelectedContracts(contracts);
        },
        [rights]
    );

    const showPanelContractDetails = (e: any) => {
        console.log(e);
        hidePanelContractDetails();
        setSelectedContract(e.row);
        setContractDetailsPanelOpen(true);
    };

    const hidePanelContractDetails = () => {
        setContractDetailsPanelOpen(false);
    };

    const additionalFilters = (
        <>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                startAdornment={<Inventory2Outlined />}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={titleTypeFilter}
                onChange={(value) => handleTitleTypeFilter(value)}
            >
                <MenuItem value={"Default"}>{t("field:label.titleTypeLowerCase")}</MenuItem>
                {titleTypes.map((status, index) => (
                    <MenuItem value={status} key={index}>
                        {status}
                    </MenuItem>
                ))}
            </Select>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                startAdornment={<CopyrightOutlined />}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={typeFilter}
                onChange={(value) => handleTypeFilter(value)}
            >
                <MenuItem value={"Default"}>{t("common:type")}</MenuItem>
                {types.map((status, index) => (
                    <MenuItem value={status} key={index}>
                        {status}
                    </MenuItem>
                ))}
            </Select>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={deliveredFilter}
                onChange={(value) => handleDeliveredFilter(value)}
            >
                <MenuItem value={"Default"}>{t("menu:item.isDelivered")}</MenuItem>
                {delivered.map((status, index) => (
                    <MenuItem value={status} key={index}>
                        {status}
                    </MenuItem>
                ))}
            </Select>
        </>
    );

    return (
        <Stack sx={{ flex: "1 1 0", minHeight: 0 }}>
            <ListPageHeader sx={{ mb: 0 }}>
                <ListPageHeaderLeft title={t("page:searchContracts")}>
                    <Autocomplete
                        options={customers}
                        value={selectedCustomer}
                        size="small"
                        onChange={(e, value) => {
                            setExternalIdFilter("");
                            setSelectedCustomer(value ?? undefined);
                            if (value) handleSearchByCustomer(value.id);
                        }}
                        getOptionLabel={(option) => option.name! + " - #" + option.customerCodeId}
                        isOptionEqualToValue={(option, value) => option.name!.includesAllWords(value.name!)}
                        renderInput={(params) => (
                            <OutlinedInput
                                {...params.InputProps}
                                inputProps={params.inputProps}
                                placeholder={t("common:customer")}
                                sx={{ width: "100%" }}
                            />
                        )}
                        sx={{ minWidth: 180, maxWidth: 300 }}
                    />

                    <OutlinedInput
                        label={t("common:standard")}
                        placeholder={t("field:placeholder.rightOrContractId")}
                        value={externalIdFilter}
                        sx={{ paddingRight: 0, width: 180 }}
                        endAdornment={
                            <InputAdornment position="end">
                                {externalIdFilter && (
                                    <IconButton
                                        aria-label="Clear field"
                                        onClick={() => {
                                            setExternalIdFilter("");
                                            setRights(initialRights);
                                        }}
                                        size="small"
                                    >
                                        <CloseRoundedIcon />
                                    </IconButton>
                                )}
                                <IconButton size="small" onClick={handleSearchByContractId}>
                                    <SearchIcon sx={{ color: "text.tier" }} />
                                </IconButton>
                            </InputAdornment>
                        }
                        onChange={(e) => setExternalIdFilter(e.currentTarget.value)}
                        onKeyDown={handleOnKeyDown}
                    />

                    <AutocompleteSearchCatalog
                        onSearchEnter={setOnSearchEnter}
                        onSearchContractCallback={setDataFromSearchCallback}
                        onClear={() => setRights(initialRights)}
                        layer={ObjectLayer.Title}
                        enableSearchCallback={true}
                        context="Contract"
                        width={180}
                    />
                </ListPageHeaderLeft>
                {/* <Stack
          sx={{ marginLeft: "0px !important" }}
          direction="row"
          justifyContent="space-between"
          width="100%"
          minWidth={1014}
        >
          <Box display="flex" flexDirection="row" columnGap={2}>
            <label style={{ lineHeight: "36px" }} htmlFor="group-by-title">
              Group by title ?
            </label>
            <Switch
              aria-labelledby="group-by-title"
              value={groupByTitle}
              onChange={(e) => {
                setGroupByTitle(e.target.checked);
                if (apiGrid.current) apiGrid.current.setPage(0);
              }}
            />
          </Box>
          <Box display="flex" flexDirection="row" columnGap={2}>
            <Autocomplete
              value={filterMediaNames || []}
              multiple
              onChange={(e, value) => setFilterMediaNames(value)}
              options={mediaNamesOptions}
              renderInput={(params) => (
                <TextField sx={{ mt: "-24px" }} {...params} placeholder="Filter by media names" />
              )}
              sx={{ minWidth: 200, maxWidth: "350px" }}
            />
          </Box>
        </Stack> */}
            </ListPageHeader>
            <DataTable
                loading={isLoading}
                tableKey={TableUuids.CONTRACTS_TABLE}
                rows={MakeUnique(
                    rights.filter(
                        (right) =>
                            (titleTypeFilter === "Default" || right.titleType === titleTypeFilter) &&
                            (typeFilter === "Default" || right.type === typeFilter) &&
                            (deliveredFilter === "Default" ||
                                (right.okDelivery === false && deliveredFilter === "False") ||
                                (right.okDelivery === true && deliveredFilter === "True")) &&
                            (filterMediaNames.length === 0 ||
                                (right.mediaNamesString !== undefined &&
                                    right.mediaNamesString.length > 0 &&
                                    filterMediaNames?.every((item) =>
                                        [
                                            ...new Set(
                                                right.mediaNamesString
                                                    ?.split(",")
                                                    .map((str) => str.trim())
                                                    .filter((x) => x !== "")
                                            ),
                                        ].includes(item)
                                    )))
                    ),
                    [groupByTitle ? "titleId" : "id"]
                )}
                columns={getColumns}
                additionalFilters={additionalFilters}
                getRowHeight={() => "auto"}
                disableRowSelectionOnClick
                getRowId={(row: ContractAndRightType) => row.id}
                onRowClick={showPanelContractDetails}
                apiRef={apiGrid}
                initialState={{
                    sorting: {
                        sortModel: [{ field: "contractId", sort: "asc" }],
                    },
                }}
                setSelectedRows={(e) => {
                    handleSetSelectedContracts(e as number[]);
                }}
                selectedRows={selectedContractTds}
                checkboxSelection
            />
            <ContractDetailsPanel
                open={contractDetailsPanelOpen}
                onClose={(e) => setContractDetailsPanelOpen(false)}
                contract={selectedContract!}
            />
        </Stack>
    );
}

export default ContractsAndRights;
